import React, { useState, useRef } from 'react';  // ← add useRef
import ImgSkeleton from "../../../../Assets/pngs/img_skeleton.png";
import { IoIosShareAlt } from "react-icons/io";
import { FiHeart } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import { LuImagePlus } from "react-icons/lu";
import { MdPlayCircle, MdPause } from "react-icons/md"; // ← add MdPause

const ImageGallery = ({ images, videos = [], property, isSaved, onFavoriteClick, onShare }) => {
  const [activeMedia, setActiveMedia] = useState(() => {
    if (images?.length > 0) return { type: "image", src: images[0] };
    if (videos?.length > 0) return { type: "video", src: videos[0].video };
    return { type: "image", src: null };
  });
  const [openStack, setOpenStack] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);  // ← add
  const [showIcon, setShowIcon] = useState(false);     // ← add for tap feedback
  const videoRef = useRef(null);                       // ← add

  const isVideo = activeMedia.type === "video";

  // ✅ Reset play state when switching media
  const switchMedia = (media) => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    setActiveMedia(media);
  };

  // ✅ Tap to toggle play/pause
  const handleVideoTap = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
    // Show icon briefly on tap
    setShowIcon(true);
    setTimeout(() => setShowIcon(false), 800);
  };

  return (
    <section className="relative">
      <div className="relative w-full aspect-square rounded-[2rem] overflow-hidden bg-gray-200">
        {isVideo ? (
          <div className="relative w-full h-full cursor-pointer" onClick={handleVideoTap}>
            <video
              ref={videoRef}
              key={activeMedia.src}
              src={activeMedia.src}
              className="h-full w-full object-cover"
              playsInline
              muted          // ← no sound
              preload="auto"
              loop           // ← loops naturally
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />

            {/* ✅ Tap feedback icon — fades in and out */}
            {showIcon && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-black/40 rounded-full p-4 animate-ping-once">
                  {isPlaying
                    ? <MdPause className="text-white" size={52} />
                    : <MdPlayCircle className="text-white" size={52} />
                  }
                </div>
              </div>
            )}

            {/* ✅ Persistent play button when paused */}
            {!isPlaying && !showIcon && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <MdPlayCircle className="text-white/80" size={56} />
              </div>
            )}

            {/* ✅ Muted indicator */}
            <div className="absolute bottom-3 right-3 bg-black/50 text-white text-[10px] p-2  rounded-full pointer-events-none">
               {isPlaying ? <MdPause className="text-white" size={20} /> : <MdPlayCircle className="text-white" size={20} /> }
            </div>
          </div>
        ) : activeMedia.src ? (
          <img
            src={activeMedia.src}
            alt={property.title}
            onError={(e) => { e.target.onerror = null; e.target.src = ImgSkeleton; }}
            className="h-full w-full object-cover"
          />
        ) : (
          <img src={ImgSkeleton} className="h-full w-full object-cover" alt="No media" />
        )}

        {/* Overlay controls — unchanged */}
        <div className="absolute top-2 md:top-4 right-0 left-0 flex justify-between items-center px-2 md:px-4">
          <span className="flex items-center gap-2 text-[10px] md:text-xs text-white bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10 shadow-[0_0_10px_rgba(34,197,94,0.3)]">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500 shadow-[0_0_8px_#22c55e]"></span>
            </span>
            <p className="font-medium">Available: {property.available_from}</p>
          </span>
          <div>
            {/* <button
              onClick={(e) => onFavoriteClick(e, property)}
              className="p-2 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all z-10"
              >
              {isSaved(property.id) ? (
                <FaHeart className="text-red-500 animate-pulse" size={18} />
              ) : (
                <FiHeart className="text-gray-600 hover:text-red-500" size={18} />
              )}
            </button> */}
            <button
              className="bg-white/80 text-gray-600 rounded-full p-2 shadow-md ml-3"
              onClick={onShare}
            >
              <IoIosShareAlt size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Stack — unchanged, just update setActiveMedia → switchMedia */}
      <div className="absolute bottom-1 right-4 left-2 lg:hidden flex items-center max-w-[90vw]">
        <div
          className={`flex gap-2 transition-all duration-300 overflow-x-auto no-scrollbar py-2 px-1
            ${openStack ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12 pointer-events-none"}`}
          style={{ maxWidth: "calc(100vw - 80px)" }}
        >
          {images.map((img, i) => (
            <button
              key={`img-${i}`}
              onClick={() => { switchMedia({ type: "image", src: img }); setOpenStack(false); }}
              className={`h-14 w-14 flex-shrink-0 rounded-2xl overflow-hidden border-2 bg-white shadow-sm
                ${activeMedia.src === img ? "border-black" : "border-white"}`}
            >
              <img src={img} className="h-full w-full object-cover" alt={`thumb-${i}`} />
            </button>
          ))}
          {videos.map((vid, i) => (
            <button
              key={`vid-${i}`}
              onClick={() => { switchMedia({ type: "video", src: vid.video }); setOpenStack(false); }}
              className={`relative h-14 w-14 flex-shrink-0 rounded-2xl overflow-hidden border-2 bg-black shadow-sm
                ${activeMedia.src === vid.video ? "border-black" : "border-white"}`}
            >
              <video src={vid.video} className="h-full w-full object-cover opacity-70" muted preload="metadata" />
              <MdPlayCircle className="absolute inset-0 m-auto text-white" size={22} />
            </button>
          ))}
        </div>

        {(images.length + videos.length) > 1 && (
          <button
            onClick={() => setOpenStack(!openStack)}
            className="relative ml-2 flex-shrink-0 w-10 h-10 md:h-12 md:w-12 rounded-full bg-black text-white z-10 shadow-lg flex items-center justify-center"
          >
            {openStack ? (
              <span className="text-xl">✕</span>
            ) : (
              <div className="relative">
                <LuImagePlus size={22} />
                <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-white text-black text-[10px] font-bold border-2 border-black">
                  +{images.length + videos.length}
                </span>
              </div>
            )}
          </button>
        )}
      </div>

      {/* Desktop Thumbnails — unchanged, just update setActiveMedia → switchMedia */}
      <div className="hidden lg:flex flex-wrap gap-1 mt-4">
        {images.map((img, i) => (
          <button
            key={`img-${i}`}
            onClick={() => switchMedia({ type: "image", src: img })}
            className={`h-24 w-24 rounded-3xl overflow-hidden border-2 ${activeMedia.src === img ? "border-black" : "border-transparent hover:border-gray-300"
              }`}
          >
            <img
              src={img}
              onError={(e) => { e.target.onerror = null; e.target.src = ImgSkeleton; }}
              className="h-full w-full object-cover"
              alt={`thumb-${i}`}
            />
          </button>
        ))}
        {videos.map((vid, i) => (
          <button
            key={`vid-${i}`}
            onClick={() => switchMedia({ type: "video", src: vid.video })}
            className={`relative h-24 w-24 rounded-3xl overflow-hidden border-2 bg-black ${activeMedia.src === vid.video ? "border-black" : "border-transparent hover:border-gray-300"
              }`}
          >
            <video src={vid.video} className="h-full w-full object-cover opacity-70" muted preload="metadata" />
            <MdPlayCircle className="absolute inset-0 m-auto text-white" size={28} />
          </button>
        ))}
      </div>
    </section>
  );
};

export default ImageGallery;